import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { changeOrdersRequestListInfo, projectDetails } from '../../../../../../core/projects.selectors';
import { ChangeOrdersRequestService } from '../change-orders-request.service';
import { ChangeOrdersRequestSideNavWrapperComponent } from '../change-orders-request-side-nav-wrapper/change-orders-request-side-nav-wrapper.component';
import { CreateChangeOrderDialogComponent } from '../create-change-order-dialog/create-change-order-dialog.component';
import {
  FetchChangeOrdersRequestListRequest,
  SendChangeOrdersRequestReminderRequest,
} from '../../../../../../core/projects.actions';
import { AuthService } from '../../../../../../../../shared/services';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';
import { Ability, subject } from '@casl/ability';

@Component({
    selector: 'app-change-orders-request-list',
    templateUrl: './change-orders-request-list.component.html',
    styleUrls: ['./change-orders-request-list.component.scss'],
    standalone: false
})
export class ChangeOrdersRequestListComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['requestNo', 'amountChange', 'workDayChange', 'status', 'actions'];
  dataSource: any = [];
  projectId: string;
  canCreate = false;
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 10,
  };
  currentPage = 1;
  totalRecords: number | undefined;
  file: any;
  selectedRow: any;
  qp: any;
  itemsPerPage = 10;
  originalDataSource = [];
  filteredDataSource: any = [];
  statusMap = {
    submitted: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'txt-submitted',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
    rejected: {
      label: 'Rejected',
      bgColor: 'bg-rejected',
      textColor: 'txt-rejected',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
  };
  startDate = null;
  endDate = null;
  statusIdsToFilter: any[] = [];
  searchText = '';
  currentUser: any;
  changeOrderCreateMode: string = 'manual';
  selectedSortColumn: string | null = 'Change order #';
  sortDirection: 'asc' | 'desc' = 'asc';
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private changeOrdersRequestService: ChangeOrdersRequestService,
    private authService: AuthService,
    private ability: Ability,
  ) {
    this.currentUser = this.authService.getCurrentUser();
  }

  ngOnInit() {
    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      if (qp['subTab'] === 'cor') {
        this.fetchChangeOrdersRequestList();
      }

      if (qp.startDate && moment(qp.startDate).isValid()) {
        this.startDate = qp.startDate;
      } else {
        this.startDate = null;
      }

      if (qp.endDate && moment(qp.endDate).isValid()) {
        this.endDate = qp.endDate;
      } else {
        this.endDate = null;
      }

      if (qp.status) {
        if (Array.isArray(qp.status)) {
          this.statusIdsToFilter = qp.status;
        } else {
          this.statusIdsToFilter = [qp.status];
        }
      } else {
        this.statusIdsToFilter = [];
      }

      this.applyFilters();
    });

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.projectId = project?.id;
        this.canCreate = this.ability.can('create', subject('ChangeOrderRequest', { project_id: this.projectId }));
        if (project && Object.keys(project).length) {
          this.changeOrderCreateMode = project?.source_entity_extra_attributes?.change_order_create_mode || 'manual';
        }
        this.fetchChangeOrdersRequestList();
      });

    this.store
      .select(changeOrdersRequestListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          // Always sync the displayed list — including the empty case after
          // deleting the last row. The previous guard (`data.list.length > 0`)
          // meant deletes that emptied the list never cleared the local cache,
          // so stale rows rendered until the user refreshed the page.
          this.originalDataSource = data?.list || [];
          this.totalRecords = data?.totalRecords || 0;
          this.applyFilters();
        }
      });

    this.commonService.clearRowSelection.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.selectedRow = null;
    });

    this.changeOrdersRequestService.search.pipe(takeUntil(this.onDestroy)).subscribe((text: string) => {
      this.searchText = text || '';
      this.applyFilters();
    });

    this.changeOrdersRequestService.sort.pipe(takeUntil(this.onDestroy)).subscribe((sort) => {
      this.selectedSortColumn = sort.column;
      this.sortDirection = sort.direction;
      this.applyFilters();
    });

    // Re-fetch list when right panel closes (after workflow actions)
    this.projectsService.closeRightPanel.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.fetchChangeOrdersRequestList();
    });
  }

  rowClicked(data) {
    this.selectedRow = data;

    this.projectsService.openRightPanel.next({
      component: ChangeOrdersRequestSideNavWrapperComponent,
      data: {
        ...data,
        createRequest: false,
      },
    });

    this.router.navigate([], {
      queryParams: {
        subTab: 'cor',
        snSubTab: 'details',
        cor: data.id,
      },
      queryParamsHandling: 'merge',
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;

    this.paginateData();
  }

  fetchChangeOrdersRequestList() {
    const payload = {
      projectId: this.projectId,
      qp: {
        start: this.pagination.pageIndex * this.pagination.pageSize,
        total: 1000,
      },
    };

    if (payload.projectId) {
      this.store.dispatch(FetchChangeOrdersRequestListRequest({ payload }));
    }
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource?.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex).sort((a, b) => {
      return a.item - b.item;
    });
  }

  applyFilters() {
    let filteredData = [...this.originalDataSource];

    if (this.startDate) {
      const start = moment(this.startDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data => moment(data.created_at, 'YYYY-MM-DD').isSameOrAfter(start));
    }

    if (this.endDate) {
      const end = moment(this.endDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data => moment(data.created_at, 'YYYY-MM-DD').isSameOrBefore(end));
    }

    if (this.statusIdsToFilter?.length) {
      filteredData = filteredData.filter(data => this.statusIdsToFilter.includes(data.status));
    }

    if (this.searchText.trim()) {
      const search = this.searchText.trim().toLowerCase();
      filteredData = filteredData.filter(
        data => data.description?.toLowerCase().includes(search) || data.order_number?.toLowerCase().includes(search)
      );
    }

    filteredData = this.sortItems(filteredData);

    this.filteredDataSource = filteredData;
    this.totalRecords = filteredData.length;
    this.paginateData(true);
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  private sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const itemsToSort = [...items];

    const safeLower = (v: any) => String(v ?? '').toLowerCase();
    const safeDate = (v: any) => {
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    if (!this.selectedSortColumn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const sortFns: Record<string, (a: any, b: any) => number> = {
      'Change order #': (a, b) => safeLower(a.order_number).localeCompare(safeLower(b.order_number), undefined, { numeric: true, sensitivity: 'base' }),
      'Amount change': (a, b) => (a.totalAmount ?? 0) - (b.totalAmount ?? 0),
      'Work days change': (a, b) => (a.totalWorkDaysChange ?? 0) - (b.totalWorkDaysChange ?? 0),
      'Status': (a, b) => safeLower(a.status).localeCompare(safeLower(b.status)),
      'Created at': (a, b) => safeDate(a.created_at) - safeDate(b.created_at),
    };

    const fn = sortFns[this.selectedSortColumn];
    if (!fn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const applyDirection = (result: number) => (this.sortDirection === 'asc' ? result : -result);

    return itemsToSort.sort((a, b) => {
      const primary = fn(a, b);
      if (primary !== 0) {
        return applyDirection(primary);
      }
      return safeDate(b.updated_at) - safeDate(a.updated_at);
    });
  }

  isCreator(row: any): boolean {
    if (!row?.createdBy || !this.currentUser?.id) {
      return false;
    }
    return row.createdBy === this.currentUser.id || row.createdBy?.id === this.currentUser.id;
  }

  shouldShowMenu(row: any): boolean {
    return (this.isCreator(row) && row?.status === 'submitted') || row?.status === 'draft';
  }

  sendReminder(row: any, event: Event): void {
    event.stopPropagation();
    this.store.dispatch(SendChangeOrdersRequestReminderRequest({ payload: { id: row.id } }));
  }

  deleteChangeOrder(row: any, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete change order?',
        `Are you sure you want to delete change order <b>${row.order_number || ''}</b>?`
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.projectsService.deleteChangeOrder({ id: row.id }).subscribe(() => {
          this.commonService.notification('Change order deleted', 'success');
          this.fetchChangeOrdersRequestList();
        });
      }
    });
  }

  openCreateDialog() {
    this.projectsService.openRightPanel.next({
      component: CreateChangeOrderDialogComponent,
      data: { projectId: this.projectId },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
